import React, { createContext, useContext, useState } from "react";
import { cropStyle, useLogoInfo } from "../../utils/trimmedLogo";

export const C = {
  bg: "#fafafa",
  panel: "#ffffff",
  panelHi: "#f3f4f6",
  border: "#e5e7eb",
  accent: "#111111",
  gold: "#b7860b",
  text: "#1a1a1a",
  muted: "#6b7280",
  faint: "rgba(17,17,17,0.035)",
  grid: "rgba(17,17,17,0.08)",
  win: "#16a34a",
  draw: "#9ca3af",
  loss: "#dc2626",
  forfeit: "#d97706",
  forecast: "#2563eb",
};

/** Accent per highlight tone: gold for goals, green good, red bad, white neutral. */
export const TONE = { gold: C.gold, good: C.win, bad: C.loss };
export const toneColor = (tone) => TONE[tone] || C.accent;

export const FONT = "Inter, Roboto, 'Helvetica Neue', Arial, sans-serif";

export const pct = (p) => (p >= 0.995 ? ">99%" : p > 0 && p < 0.005 ? "<1%" : `${Math.round(p * 100)}%`);

export function initials(name) {
  const words = String(name || "?")
    .replace(/\bF\.?C\.?\b/gi, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  return (words.length === 1 ? words[0].slice(0, 2) : words[0][0] + words[1][0]).toUpperCase();
}

/** url → data URL. The poster is captured with every image inlined so nothing is fetched at capture time. */
export const InlineImages = createContext({});
export const useInlineImage = (url) => {
  const map = useContext(InlineImages);
  return (url && map[url]) || url;
};

/** Fetches each image once and returns { url: dataUrl }. Images that fail are left out (initials fallback). */
export async function inlineImages(urls) {
  const unique = [...new Set(urls.filter(Boolean))];
  const pairs = await Promise.all(
    unique.map(async (url) => {
      try {
        const res = await fetch(url, { mode: "cors", cache: "force-cache" });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        const data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        return [url, data];
      } catch (e) {
        return [url, null];
      }
    })
  );
  return Object.fromEntries(pairs.filter(([, d]) => d));
}

/** Team crest, falling back to initials when there is no logo or it fails to load. */
export function Crest({ url, name, size = 28, ring, style }) {
  const [failed, setFailed] = useState(false);
  const src = useInlineImage(url);
  const info = useLogoInfo(url);
  const base = {
    width: size,
    height: size,
    flex: "0 0 auto",
    borderRadius: "50%",
    boxSizing: "border-box",
    ...(ring ? { boxShadow: `0 0 0 2px ${ring}` } : null),
    ...style,
  };
  if (!url || failed) {
    return (
      <div
        title={name}
        style={{
          ...base,
          background: C.panelHi,
          color: C.text,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: Math.max(9, size * 0.36),
          fontWeight: 800,
          fontFamily: FONT,
          border: `1px solid ${C.border}`,
        }}
      >
        {initials(name)}
      </div>
    );
  }
  return (
    <div
      title={name}
      style={{
        ...base,
        position: "relative",
        overflow: "hidden",
        borderRadius: ring || info?.light ? "24%" : 0,
        ...(info?.light ? { background: "#18181b" } : null),
      }}
    >
      <div style={{ position: "absolute", inset: info?.light ? size * 0.12 : 0, overflow: "hidden" }}>
        <img
          src={src}
          alt={name || ""}
          crossOrigin={src && src.startsWith("data:") ? undefined : "anonymous"}
          onError={() => setFailed(true)}
          style={{ display: "block", ...cropStyle(info) }}
        />
      </div>
    </div>
  );
}

export const RESULT_COLORS = { W: C.win, D: C.draw, L: C.loss, F: C.forfeit };

export function FormPill({ r, size = 20 }) {
  return (
    <span
      title={{ W: "Win", D: "Draw", L: "Loss", F: "Forfeit" }[r]}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        borderRadius: 4,
        background: RESULT_COLORS[r],
        color: "#fff",
        fontSize: size * 0.55,
        fontWeight: 800,
        fontFamily: FONT,
      }}
    >
      {r}
    </span>
  );
}

export function formatMatchdayDate(iso, opts = { weekday: "short", month: "short", day: "numeric", year: "numeric" }) {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", opts);
}

export const joinNames = (names) =>
  names.length <= 1 ? names[0] || "" : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;

/** Colours for a predicted score: green for the likely winner, red for the likely loser, grey for a draw. */
export function outcomeColors(homeGoals, awayGoals) {
  if (homeGoals === awayGoals) return [C.draw, C.draw];
  return homeGoals > awayGoals ? [C.win, C.loss] : [C.loss, C.win];
}

/** Win / draw / win bar: the favourite's share green, the underdog's red, the draw grey. */
export function OddsBar({ home, draw, away, height = 6 }) {
  const [h, a] = home === away ? [C.draw, C.draw] : home > away ? [C.win, C.loss] : [C.loss, C.win];
  return (
    <div style={{ display: "flex", height, borderRadius: height / 2, overflow: "hidden", background: C.panelHi, flex: 1 }}>
      <span style={{ width: `${home * 100}%`, background: h }} />
      <span style={{ width: `${draw * 100}%`, background: C.draw, opacity: 0.6 }} />
      <span style={{ width: `${away * 100}%`, background: a }} />
    </div>
  );
}

/** Predicted score with each side coloured by its likely outcome. */
export function PredictedScore({ score, size = 14 }) {
  const [hc, ac] = outcomeColors(score[0], score[1]);
  return (
    <span style={{ fontWeight: 900, fontSize: size, whiteSpace: "nowrap" }}>
      <span style={{ color: hc }}>{score[0]}</span>
      <span style={{ color: C.muted }}>–</span>
      <span style={{ color: ac }}>{score[1]}</span>
    </span>
  );
}
